// Menu configuration: Drives navigation, submenus, labels, and rendering order.
// Enables data-driven refactors in MenuPage.tsx without hardcoding.

export type Location = "montreal" | "delson";

export interface AddOn {
  name: string;
  nameFr: string;
  price: number;
}

export interface SubmenuAddOns {
  submenu: string;
  addOns: AddOn[];
}

export interface SubmenuInfo {
  submenu: string;
  info: string;
  infoFr: string;
}

interface SectionConfig {
  label: {
    en: string;
    fr: string;
  };
  submenus: readonly string[];
  order: number;
}

export type MenuConfigs = {
  [K in Location]: Record<string, SectionConfig>;
};

export const menuConfigs: MenuConfigs = {
  montreal: {
    lunch: {
      label: { en: "LUNCH", fr: "MIDI" },
      submenus: [
        "appetizers",
        "main-dishes",
        "pitas",
        "salads",
        "pasta",
        "extras",
        "beverages",
        "desserts",
      ] as const,
      order: 0,
    },
    dinner: {
      label: { en: "DINNER", fr: "SOIR" },
      submenus: [
        "appetizers",
        "salads",
        "pasta",
        "special-cava",
        "red-meat",
        "seafood",
        "surf-turf",
        "kids-menu",
      ] as const,
      order: 1,
    },
  },
  delson: {
    "new-arrivals": {
      label: { en: "NEW ARRIVALS", fr: "NOUVEAUTÉS" },
      submenus: ["delson-cava-new-plates"] as const,
      order: 2,
    },
    lunch: {
      label: { en: "LUNCH", fr: "MIDI" },
      submenus: [
        "delson-lunch-appetizers",
        "delson-lunch-salads",
        "delson-lunch-pasta",
        "delson-lunch-main-dishes",
        "delson-lunch-midi-sandwich",
        "delson-lunch-midi-plate",
        "delson-lunch-beverages",
        "delson-lunch-desserts",
        "delson-lunch-extras",
      ] as const,
      order: 0,
    },
    dinner: {
      label: { en: "DINNER", fr: "SOIR" },
      submenus: [
        "delson-dinner-appetizers",
        "delson-dinner-special-cava",
        "delson-dinner-red-meat",
        "delson-dinner-seafood",
        "delson-dinner-surf-turf",
        "delson-dinner-pasta",
        "delson-dinner-salads",
        "delson-dinner-pitas-sandwich",
        "delson-dinner-pitas-plate",
        "delson-dinner-kids-menu",
        "delson-dinner-beverages",
        "delson-dinner-desserts",
        "delson-dinner-extras",
      ] as const,
      order: 1,
    },
    specials: {
      label: { en: "SPECIALS", fr: "SPÉCIALES" },
      submenus: ["delson-new-pita-specials"] as const,
      order: 3,
    },
    grocery: {
      label: { en: "GROCERY", fr: "ÉPICERIE" },
      submenus: [
        "delson-soups",
        "delson-dips",
        "delson-sauces",
        "delson-prepared-meals",
      ] as const,
      order: 4,
    },
  },
} as const;

// Comprehensive submenu labels - ALL submenus must be listed here
export const submenuLabels: Record<string, { en: string; fr: string }> = {
  // Montreal submenus
  appetizers: { en: "Appetizers", fr: "Les entrées" },
  "main-dishes": { en: "Main Dishes", fr: "Les plats principaux" },
  pitas: { en: "Pitas", fr: "Pitas" },
  salads: { en: "Meal Salads", fr: "Les salades repas" },
  pasta: { en: "Pasta", fr: "Les pâtes" },
  extras: { en: "Extras", fr: "Extras" },
  beverages: { en: "Beverages", fr: "Breuvages" },
  desserts: { en: "Desserts", fr: "Desserts" },
  "kids-menu": {
    en: "Kids Menu (12 and under)",
    fr: "Menu pour les enfants (12 ans et moins)",
  },
  "special-cava": { en: "Cava Special", fr: "Spécial Cava" },
  "red-meat": { en: "Red Meat", fr: "Viande Rouge" },
  seafood: { en: "Seafood", fr: "Fruits de mer" },
  "surf-turf": { en: "Surf & Turf", fr: "Terre et mer" },

  // Delson Lunch submenus
  "delson-lunch-appetizers": { en: "Appetizers", fr: "Les entrées" },
  "delson-lunch-salads": { en: "Meal Salads", fr: "Les salades repas" },
  "delson-lunch-pasta": { en: "Pasta", fr: "Les pâtes" },
  "delson-lunch-main-dishes": { en: "Main Dishes", fr: "Les plats principaux" },
  "delson-lunch-midi-sandwich": {
    en: "Sandwich Only",
    fr: "Sandwich seulement",
  },
  "delson-lunch-midi-plate": { en: "Plate", fr: "Assiette" },
  "delson-lunch-beverages": { en: "Beverages", fr: "Breuvages" },
  "delson-lunch-desserts": { en: "Desserts", fr: "Desserts" },
  "delson-lunch-extras": { en: "Extras", fr: "Extras" },

  // Delson Dinner submenus
  "delson-dinner-appetizers": { en: "Appetizers", fr: "Les entrées" },
  "delson-dinner-special-cava": { en: "Cava Special", fr: "Spécial Cava" },
  "delson-dinner-red-meat": { en: "Red Meat", fr: "Viande Rouge" },
  "delson-dinner-seafood": { en: "Seafood", fr: "Fruits de mer" },
  "delson-dinner-surf-turf": { en: "Surf & Turf", fr: "Terre et mer" },
  "delson-dinner-pasta": { en: "Pasta", fr: "Les pâtes" },
  "delson-dinner-salads": { en: "Meal Salads", fr: "Les salades repas" },
  "delson-dinner-pitas-sandwich": {
    en: "Sandwich Only",
    fr: "Sandwich seulement",
  },
  "delson-dinner-pitas-plate": { en: "Plate", fr: "Assiette" },
  "delson-dinner-kids-menu": {
    en: "Kids Menu (12 and under)",
    fr: "Menu pour les enfants (12 ans et moins)",
  },
  "delson-dinner-beverages": { en: "Beverages", fr: "Breuvages" },
  "delson-dinner-desserts": { en: "Desserts", fr: "Desserts" },
  "delson-dinner-extras": { en: "Extras", fr: "Extras" },

  // Delson New Arrivals
  "delson-cava-new-plates": {
    en: "Cava New Plates",
    fr: "Nouveaux Plats Cava",
  },

  // Delson Specials
  "delson-new-pita-specials": {
    en: "New Pita Specials",
    fr: "Nouveaux Spéciaux Pitas",
  },

  // Delson Grocery
  "delson-soups": { en: "Soups", fr: "Soupes" },
  "delson-dips": { en: "Dips", fr: "Trempettes" },
  "delson-sauces": { en: "Sauces", fr: "Sauces" },
  "delson-prepared-meals": { en: "Prepared Meals", fr: "Plats préparés" },
};

// Infer section names per location
export type SectionName<L extends Location> = keyof MenuConfigs[L];

// Get config for a section
export function getSectionConfig<L extends Location>(
  location: L,
  section: SectionName<L>,
): MenuConfigs[L][SectionName<L>] {
  const config = menuConfigs[location][section];
  if (!config) {
    throw new Error(
      `Invalid section "${String(section)}" for location "${location}"`,
    );
  }
  return config;
}

// Get sorted nav sections for a location
export function getNavSections<L extends Location>(
  location: L,
): SectionName<L>[] {
  return Object.entries(menuConfigs[location])
    .sort(([, a], [, b]) => a.order - b.order)
    .map(([key]) => key as SectionName<L>);
}

// Get label (with language fallback)
export function getSectionLabel<L extends Location>(
  location: L,
  section: SectionName<L>,
  lang: "en" | "fr" = "en",
): string {
  return getSectionConfig(location, section).label[lang];
}

// Get submenu label with language support
export function getSubmenuLabel(
  submenu: string,
  language: "en" | "fr" = "en",
): string {
  return submenuLabels[submenu]?.[language] || submenu;
}

// Get all submenus for a location and section
export function getSubmenusForSection<L extends Location>(
  location: L,
  section: SectionName<L>,
): readonly string[] {
  return getSectionConfig(location, section).submenus;
}

// Validate that a submenu exists in the labels
export function isValidSubmenu(submenu: string): boolean {
  return submenu in submenuLabels;
}

// Get all unique submenus across all locations
export function getAllSubmenus(): string[] {
  const allSubmenus = new Set<string>();
  for (const location of Object.keys(menuConfigs) as Location[]) {
    for (const section of Object.keys(menuConfigs[location])) {
      const config = menuConfigs[location][section];
      for (const submenu of config.submenus) {
        allSubmenus.add(submenu);
      }
    }
  }
  return Array.from(allSubmenus);
}

// Add-ons configuration for specific submenus
export const submenuAddOns: Record<Location, Record<string, SubmenuAddOns>> = {
  montreal: {
    "lunch-main-dishes": {
      submenu: "main-dishes",
      addOns: [
        { name: "Caesar Salad", nameFr: "Salade César", price: 2.95 },
        { name: "Greek Salad", nameFr: "Salade Grecque", price: 3.95 },
        { name: "Dessert", nameFr: "Dessert", price: 3.0 },
      ],
    },
    "lunch-pitas": {
      submenu: "pitas",
      addOns: [
        { name: "Caesar Salad", nameFr: "Salade César", price: 2.95 },
        { name: "Greek Salad", nameFr: "Salade Grecque", price: 4.95 },
      ],
    },
    "dinner-special-cava": {
      submenu: "special-cava",
      addOns: [
        {
          name: "Table d'hôte (Includes Coffee or Tea and Dessert)",
          nameFr: "Table d'hôte (Inclus Café ou Thé et Dessert)",
          price: 6.0,
        },
      ],
    },
    "dinner-red-meat": {
      submenu: "red-meat",
      addOns: [
        {
          name: "Table d'hôte (Includes Coffee or Tea and Dessert)",
          nameFr: "Table d'hôte (Inclus Café ou Thé et Dessert)",
          price: 6.0,
        },
      ],
    },
    "dinner-seafood": {
      submenu: "seafood",
      addOns: [
        {
          name: "Table d'hôte (Includes Coffee or Tea and Dessert)",
          nameFr: "Table d'hôte (Inclus Café ou Thé et Dessert)",
          price: 6.0,
        },
      ],
    },
    "dinner-surf-turf": {
      submenu: "surf-turf",
      addOns: [
        {
          name: "Table d'hôte (Includes Coffee or Tea and Dessert)",
          nameFr: "Table d'hôte (Inclus Café ou Thé et Dessert)",
          price: 6.0,
        },
      ],
    },
  },
  delson: {
    "lunch-delson-lunch-midi-sandwich": {
      submenu: "delson-lunch-midi-sandwich",
      addOns: [
        { name: "Caesar Salad", nameFr: "Salade César", price: 2.95 },
        { name: "Greek Salad", nameFr: "Salade Grecque", price: 4.95 },
      ],
    },
    "lunch-delson-lunch-midi-plate": {
      submenu: "delson-lunch-midi-plate",
      addOns: [
        { name: "Caesar Salad", nameFr: "Salade César", price: 2.95 },
        { name: "Greek Salad", nameFr: "Salade Grecque", price: 4.95 },
      ],
    },
    "lunch-delson-lunch-main-dishes": {
      submenu: "delson-lunch-main-dishes",
      addOns: [
        { name: "Caesar Salad", nameFr: "Salade César", price: 2.95 },
        { name: "Greek Salad", nameFr: "Salade Grecque", price: 4.95 },
        { name: "Dessert", nameFr: "Dessert", price: 3.0 },
      ],
    },
    "dinner-delson-dinner-pitas-sandwich": {
      submenu: "delson-dinner-pitas-sandwich",
      addOns: [
        { name: "Caesar Salad", nameFr: "Salade César", price: 2.95 },
        { name: "Greek Salad", nameFr: "Salade Grecque", price: 4.95 },
      ],
    },
    "dinner-delson-dinner-pitas-plate": {
      submenu: "delson-dinner-pitas-plate",
      addOns: [
        { name: "Caesar Salad", nameFr: "Salade César", price: 2.95 },
        { name: "Greek Salad", nameFr: "Salade Grecque", price: 4.95 },
      ],
    },
    "dinner-delson-dinner-special-cava": {
      submenu: "delson-dinner-special-cava",
      addOns: [
        { name: "Caesar Salad", nameFr: "Salade César", price: 2.95 },
        { name: "Greek Salad", nameFr: "Salade Grecque", price: 4.95 },
        {
          name: "Table d'hôte (Includes Coffee or Tea and Dessert)",
          nameFr: "Table d'hôte (Inclus Café ou Thé et Dessert)",
          price: 6.0,
        },
      ],
    },
    "dinner-delson-dinner-red-meat": {
      submenu: "delson-dinner-red-meat",
      addOns: [
        { name: "Caesar Salad", nameFr: "Salade César", price: 2.95 },
        { name: "Greek Salad", nameFr: "Salade Grecque", price: 4.95 },
        {
          name: "Table d'hôte (Includes Coffee or Tea and Dessert)",
          nameFr: "Table d'hôte (Inclus Café ou Thé et Dessert)",
          price: 6.0,
        },
      ],
    },
    "dinner-delson-dinner-seafood": {
      submenu: "delson-dinner-seafood",
      addOns: [
        { name: "Caesar Salad", nameFr: "Salade César", price: 2.95 },
        { name: "Greek Salad", nameFr: "Salade Grecque", price: 4.95 },
        {
          name: "Table d'hôte (Includes Coffee or Tea and Dessert)",
          nameFr: "Table d'hôte (Inclus Café ou Thé et Dessert)",
          price: 6.0,
        },
      ],
    },
    "dinner-delson-dinner-surf-turf": {
      submenu: "delson-dinner-surf-turf",
      addOns: [
        { name: "Caesar Salad", nameFr: "Salade César", price: 2.95 },
        { name: "Greek Salad", nameFr: "Salade Grecque", price: 4.95 },
        {
          name: "Table d'hôte (Includes Coffee or Tea and Dessert)",
          nameFr: "Table d'hôte (Inclus Café ou Thé et Dessert)",
          price: 6.0,
        },
      ],
    },
  },
};

// Submenu info/notes configuration
export const submenuInfos: Record<Location, Record<string, SubmenuInfo>> = {
  montreal: {
    "dinner-special-cava": {
      submenu: "special-cava",
      info: "All plates are served with house rice, Greek-style potatoes, soup or chef's salad",
      infoFr:
        "Tous les plats sont servis avec riz maison, patates grecques, soupe ou salade du chef",
    },
    "dinner-red-meat": {
      submenu: "red-meat",
      info: "All plates are served with house rice, Greek-style potatoes, soup or chef's salad",
      infoFr:
        "Tous les plats sont servis avec riz maison, patates grecques, soupe ou salade du chef",
    },
    "dinner-seafood": {
      submenu: "seafood",
      info: "All plates are served with house rice, Greek-style potatoes, soup or chef's salad",
      infoFr:
        "Tous les plats sont servis avec riz maison, patates grecques, soupe ou salade du chef",
    },
    "dinner-surf-turf": {
      submenu: "surf-turf",
      info: "All plates are served with house rice, Greek-style potatoes, soup or chef's salad",
      infoFr:
        "Tous les plats sont servis avec riz maison, patates grecques, soupe ou salade du chef",
    },
    "dinner-kids-menu": {
      submenu: "kids-menu",
      info: "All kids' meals (12 years and under) include a dessert",
      infoFr:
        "Tous les plats pour enfants (12 ans et moins) incluent un dessert",
    },
  },

  delson: {
    "lunch-delson-lunch-main-dishes": {
      submenu: "delson-lunch-main-dishes",
      info: "All plates are served with house rice, Greek-style potatoes, soup or chef's salad, coffee or tea",
      infoFr:
        "Tous les plats sont servis avec riz maison, patates grecques, soupe ou salade du chef, café ou thé",
    },
    "lunch-delson-lunch-midi-plate": {
      submenu: "delson-lunch-midi-plate",
      info: "The meal plates are served with homemade rice, Greek potatoes and chef's salad",
      infoFr:
        "Les assiettes repas sont servies avec riz maison, patates grecques et salade du chef",
    },
    "lunch-delson-lunch-pasta": {
      submenu: "delson-lunch-pasta",
      info: "Soup included with salads and pastas meals",
      infoFr: "Soupe incluse avec les salades repas et les pâtes",
    },
    "lunch-delson-lunch-salads": {
      submenu: "delson-lunch-salads",
      info: "Soup included with salads and pastas meals",
      infoFr: "Soupe incluse avec les salades repas et les pâtes",
    },
    "dinner-delson-dinner-kids-menu": {
      submenu: "delson-dinner-kids-menu",
      info: "All kids' meals include a dessert",
      infoFr: "Tous les plats pour enfants incluent un dessert",
    },
    "dinner-delson-dinner-special-cava": {
      submenu: "delson-dinner-special-cava",
      info: "All plates are served with house rice, Greek-style potatoes, soup or chef's salad",
      infoFr:
        "Tous les plats sont servis avec riz maison, patates grecques, soupe ou salade du chef",
    },
    "dinner-delson-dinner-red-meat": {
      submenu: "delson-dinner-red-meat",
      info: "All plates are served with house rice, Greek-style potatoes, soup or chef's salad",
      infoFr:
        "Tous les plats sont servis avec riz maison, patates grecques, soupe ou salade du chef",
    },
    "dinner-delson-dinner-seafood": {
      submenu: "delson-dinner-seafood",
      info: "All plates are served with house rice, Greek-style potatoes, soup or chef's salad",
      infoFr:
        "Tous les plats sont servis avec riz maison, patates grecques, soupe ou salade du chef",
    },
    "dinner-delson-dinner-surf-turf": {
      submenu: "delson-dinner-surf-turf",
      info: "All plates are served with house rice, Greek-style potatoes, soup or chef's salad",
      infoFr:
        "Tous les plats sont servis avec riz maison, patates grecques, soupe ou salade du chef",
    },
    "dinner-delson-dinner-pasta": {
      submenu: "delson-dinner-pasta",
      info: "Soup included with salads and pastas meals",
      infoFr: "Soupe incluse avec les salades repas et les pâtes",
    },
    "dinner-delson-dinner-salads": {
      submenu: "delson-dinner-salads",
      info: "Soup included with salads and pastas meals",
      infoFr: "Soupe incluse avec les salades repas et les pâtes",
    },
    "dinner-delson-dinner-pitas-plate": {
      submenu: "delson-dinner-pitas-plate",
      info: "The meal plates are served with homemade rice, Greek potatoes and chef's salad",
      infoFr:
        "Les assiettes repas sont servies avec riz maison, patates grecques et salade du chef",
    },
  },
};

// Get add-ons for a specific submenu
export function getSubmenuAddOns(
  location: Location,
  section: string,
  submenu: string,
): AddOn[] | null {
  const key = `${section}-${submenu}`;
  const addOnsConfig = submenuAddOns[location][key];
  if (addOnsConfig && addOnsConfig.submenu === submenu) {
    return addOnsConfig.addOns;
  }
  return null;
}

// Get info for a specific submenu
export function getSubmenuInfo(
  location: Location,
  section: string,
  submenu: string,
): { info: string; infoFr: string } | null {
  const key = `${section}-${submenu}`;
  const infoConfig = submenuInfos[location][key];
  if (infoConfig && infoConfig.submenu === submenu) {
    return { info: infoConfig.info, infoFr: infoConfig.infoFr };
  }
  return null;
}
